import { CarOutlined, DeleteOutlined, ImportOutlined, SwapOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { api } from "../api/client";
import type { Page } from "../api/client";

/**
 * R31 UI requirement «цвета операций»: приёмка — зелёный, отгрузка — синий, перемещение — жёлтый, списание — красный.
 * The colour is never alone: the tag carries the word and the icon too (WCAG «use of colour»).
 */
export type OperationKind = "receive" | "ship" | "move" | "writeoff";

const KIND_OF: Record<string, OperationKind> = {
  receipt: "receive",
  receive: "receive",
  expected_receipt: "receive",
  expected_receipts: "receive",
  shipment: "ship",
  ship: "ship",
  shipment_order: "ship",
  shipment_orders: "ship",
  move: "move",
  move_mx: "move",
  placement: "move",
  replenish: "move",
  stock_move: "move",
  adjust_out: "writeoff",
  writeoff: "writeoff",
};

const ICON: Record<OperationKind, ReactNode> = {
  receive: <ImportOutlined aria-hidden />,
  ship: <CarOutlined aria-hidden />,
  move: <SwapOutlined aria-hidden />,
  writeoff: <DeleteOutlined aria-hidden />,
};

/** The operation behind a value of a «kind / type / document type / operation / entity» column, if it is one. */
export function operationOf(value: unknown): OperationKind | null {
  return typeof value === "string" ? KIND_OF[value] ?? null : null;
}

/** Field names whose values name an operation or a document type (the colour applies only there). */
export const OPERATION_FIELDS = new Set(["kind", "type", "document_type", "operation", "entity"]);

export function OperationTag({ kind, children }: { kind: OperationKind; children: ReactNode }) {
  return (
    <span className={`wms-op wms-op-${kind}`}>
      {ICON[kind]}
      {children}
    </span>
  );
}

/**
 * «Цвет документа» (29.10.2026): the list row and the card of a document are tinted in the colour of its operation —
 * поступление светло-зелёное, отгрузка светло-синяя, перемещение светло-жёлтое, брак и списание светло-красные.
 */
const DOCUMENT_OF: Record<string, OperationKind> = {
  expected_receipts: "receive",
  shipment_orders: "ship",
  waves: "ship",
  cargo_units: "ship",
  location_moves: "move",
};

/** Journals that mix operations: the row's own field names its operation (tasks as on the terminal). */
const ROW_FIELD: Record<string, string> = {
  stock_movements: "kind",
  integration_drafts: "kind",
  tasks: "type",
};

export const OPERATION_WORD: Record<OperationKind, string> = {
  receive: "Поступление",
  ship: "Отгрузка",
  move: "Перемещение",
  writeoff: "Списание",
};

/** Whether the rows of `entity` differ in colour (a legend explains them). */
export function mixesOperations(entity: string): boolean {
  return entity in ROW_FIELD;
}

type AnyRow = Record<string, unknown>;

/** The other half of one state change: the same goods, cell and batch, the opposite quantity, posted together. */
function isHalfOf(row: AnyRow, other: AnyRow): boolean {
  return (
    other.id !== row.id &&
    other.kind === "state_change" &&
    other.storage_object_id === row.storage_object_id &&
    other.location_id === row.location_id &&
    (other.batch_id ?? null) === (row.batch_id ?? null) &&
    Number(other.quantity) === -Number(row.quantity) &&
    Math.abs(Date.parse(String(other.at)) - Date.parse(String(row.at))) < 2000
  );
}

/**
 * The operation of a document row. A change of state into an unusable one («Брак») counts as a write-off: both of its
 * rows (−годен, +брак) are red, found among `neighbours`; taking goods back out of «Брак» is not.
 */
export function documentOperation(entity: string, row: AnyRow | undefined, defectStates: Set<string>, neighbours: AnyRow[] = []): OperationKind | null {
  const own = DOCUMENT_OF[entity];
  if (own) return own;
  const field = ROW_FIELD[entity];
  if (!field || !row) return null;
  if (row[field] === "state_change") {
    const incoming = Number(row.quantity) > 0 ? row : neighbours.find((n) => isHalfOf(row, n));
    return incoming && defectStates.has(String(incoming.state_id)) ? "writeoff" : null;
  }
  return operationOf(row[field]);
}

/** The word on the card's chip: a state change into «Брак» is named so, not «Списание». */
export function documentWord(kind: OperationKind, row: Record<string, unknown> | undefined): string {
  return kind === "writeoff" && row?.kind === "state_change" ? "Брак" : OPERATION_WORD[kind];
}

/** Row → operation for `entity`; the movement journal looks the defect states up once (no access → no red rows). */
export function useDocumentOperation(entity: string) {
  const defects = useQuery({
    queryKey: ["object_states", "unusable"],
    queryFn: () => api.get<Page<{ id: string }>>("/object_states", { size: 500, filter: { field: "kind", op: "eq", value: "unusable" } }),
    enabled: entity === "stock_movements",
    staleTime: 5 * 60_000,
    retry: false,
  });
  const ids = useMemo(() => new Set((defects.data?.items ?? []).map((s) => s.id)), [defects.data]);
  return useCallback((row?: AnyRow, neighbours?: AnyRow[]) => documentOperation(entity, row, ids, neighbours), [entity, ids]);
}

/** The operation of one card; a state-change row looks its other half up (in a list it is on the same page). */
export function useCardOperation(entity: string, row: AnyRow | undefined): OperationKind | null {
  const operationOfRow = useDocumentOperation(entity);
  const halves = useQuery({
    queryKey: ["stock_movements", "halves", row?.id],
    queryFn: () =>
      api.get<Page<AnyRow>>("/stock_movements", {
        size: 20,
        sort: "-at",
        filter: {
          and: [
            { field: "kind", op: "eq", value: "state_change" },
            { field: "storage_object_id", op: "eq", value: row?.storage_object_id },
            { field: "location_id", op: "eq", value: row?.location_id },
          ],
        },
      }),
    enabled: entity === "stock_movements" && row?.kind === "state_change" && Number(row?.quantity) < 0,
    staleTime: 60_000,
  });
  return operationOfRow(row, halves.data?.items);
}

/** Legend of a journal whose rows mix operations. */
export function OperationLegend() {
  return (
    <div className="wms-legend" style={{ paddingTop: 0 }}>
      <span>Цвет строки:</span>
      <OperationTag kind="receive">Поступление</OperationTag>
      <OperationTag kind="ship">Отгрузка</OperationTag>
      <OperationTag kind="move">Перемещение</OperationTag>
      <OperationTag kind="writeoff">Брак и списание</OperationTag>
    </div>
  );
}
